/**
 * Every screen in spec §9: its route, title, sidebar group and icon. The router (routes.tsx),
 * the sidebar, the breadcrumb and the e2e smoke test all read this one list.
 * Pure data: no React, no window, no import.meta.env, so Node (Playwright) can import it.
 */

export type NavGroup = 'Overview' | 'Google' | 'AI Search' | 'Content Studio' | 'Research' | 'Settings';

export type IconName =
  | 'grid' | 'dashboard' | 'flag' | 'report' | 'trend' | 'link' | 'pulse' | 'pin' | 'search'
  | 'venn' | 'pen' | 'sparkle' | 'chat' | 'quote' | 'globe' | 'idcard' | 'shield' | 'bars'
  | 'key' | 'compass' | 'sliders' | 'queue' | 'tree' | 'voice' | 'checklist' | 'at';

export type PageKey =
  | 'overview' | 'dashboard' | 'opportunities' | 'reports'
  | 'rankings' | 'backlinks' | 'audit' | 'local' | 'searchConsole' | 'gaps' | 'content'
  | 'ai' | 'prompts' | 'answers' | 'sources' | 'mentions' | 'facts' | 'readiness' | 'traffic'
  | 'studio' | 'topics' | 'voice' | 'draftCheck'
  | 'research' | 'explorer' | 'settings';

export type PageDef = {
  key: PageKey;
  /** Route pattern. Brand screens use `:brand` (the brand id). */
  path: string;
  /** Page heading and breadcrumb. */
  title: string;
  /** Sidebar label. */
  nav: string;
  group: NavGroup;
  icon: IconName;
  /** True when the screen belongs to one brand (`/b/:brand/...`). */
  brand: boolean;
  /** Only active on an exact match (paths that prefix other paths). */
  end?: boolean;
};

export const PAGES: PageDef[] = [
  { key: 'overview', path: '/', title: 'All Brands', nav: 'All Brands', group: 'Overview', icon: 'grid', brand: false, end: true },
  { key: 'dashboard', path: '/b/:brand', title: 'Dashboard', nav: 'Dashboard', group: 'Overview', icon: 'dashboard', brand: true, end: true },
  { key: 'opportunities', path: '/b/:brand/opportunities', title: 'Opportunities', nav: 'Opportunities', group: 'Overview', icon: 'flag', brand: true },
  { key: 'reports', path: '/b/:brand/reports', title: 'Reports', nav: 'Reports', group: 'Overview', icon: 'report', brand: true },

  { key: 'rankings', path: '/b/:brand/rankings', title: 'Rankings', nav: 'Rankings', group: 'Google', icon: 'trend', brand: true },
  { key: 'backlinks', path: '/b/:brand/backlinks', title: 'Backlinks', nav: 'Backlinks', group: 'Google', icon: 'link', brand: true },
  { key: 'audit', path: '/b/:brand/audit', title: 'Site Audit', nav: 'Site Audit', group: 'Google', icon: 'pulse', brand: true },
  { key: 'local', path: '/b/:brand/local', title: 'Local', nav: 'Local', group: 'Google', icon: 'pin', brand: true },
  { key: 'searchConsole', path: '/b/:brand/search-console', title: 'Search Console', nav: 'Search Console', group: 'Google', icon: 'search', brand: true },
  { key: 'gaps', path: '/b/:brand/gaps', title: 'Gaps', nav: 'Gaps', group: 'Google', icon: 'venn', brand: true },

  { key: 'ai', path: '/b/:brand/ai', title: 'AI Overview', nav: 'AI Overview', group: 'AI Search', icon: 'sparkle', brand: true, end: true },
  { key: 'prompts', path: '/b/:brand/ai/prompts', title: 'Prompts', nav: 'Prompts', group: 'AI Search', icon: 'chat', brand: true },
  { key: 'answers', path: '/b/:brand/ai/answers', title: 'Answers', nav: 'Answers', group: 'AI Search', icon: 'quote', brand: true },
  { key: 'sources', path: '/b/:brand/ai/sources', title: 'Sources', nav: 'Sources', group: 'AI Search', icon: 'globe', brand: true },
  { key: 'mentions', path: '/b/:brand/ai/mentions', title: 'Web Mentions', nav: 'Web Mentions', group: 'AI Search', icon: 'at', brand: true },
  { key: 'facts', path: '/b/:brand/ai/facts', title: 'Brand Facts', nav: 'Brand Facts', group: 'AI Search', icon: 'idcard', brand: true },
  { key: 'readiness', path: '/b/:brand/ai/readiness', title: 'Readiness', nav: 'Readiness', group: 'AI Search', icon: 'shield', brand: true },
  { key: 'traffic', path: '/b/:brand/ai/traffic', title: 'AI Traffic', nav: 'AI Traffic', group: 'AI Search', icon: 'bars', brand: true },

  { key: 'studio', path: '/b/:brand/studio', title: 'Content Plan', nav: 'Content Plan', group: 'Content Studio', icon: 'queue', brand: true, end: true },
  { key: 'topics', path: '/b/:brand/studio/topics', title: 'Topic Map', nav: 'Topic Map', group: 'Content Studio', icon: 'tree', brand: true },
  { key: 'voice', path: '/b/:brand/studio/voice', title: 'Voice & Style', nav: 'Voice & Style', group: 'Content Studio', icon: 'voice', brand: true },
  { key: 'content', path: '/b/:brand/content', title: 'Briefs & Grades', nav: 'Briefs & Grades', group: 'Content Studio', icon: 'pen', brand: true },
  { key: 'draftCheck', path: '/b/:brand/studio/check', title: 'Draft Check', nav: 'Draft Check', group: 'Content Studio', icon: 'checklist', brand: true },

  { key: 'research', path: '/research', title: 'Keyword Research', nav: 'Keyword Research', group: 'Research', icon: 'key', brand: false },
  { key: 'explorer', path: '/explorer', title: 'Domain Explorer', nav: 'Domain Explorer', group: 'Research', icon: 'compass', brand: false },

  { key: 'settings', path: '/settings', title: 'Settings', nav: 'Settings', group: 'Settings', icon: 'sliders', brand: false },
];

export const NAV_GROUPS: NavGroup[] = ['Overview', 'Google', 'AI Search', 'Content Studio', 'Research', 'Settings'];

/** The e2e fixture brand ("Sample Brand", example.com). Used only to build ROUTE_PATHS. */
export const SAMPLE_BRAND_ID = 'b5a1e000-0000-4000-8000-000000000001';

/** One concrete URL per screen, with the fixture brand in place of `:brand`. The smoke test visits each. */
export const ROUTE_PATHS: string[] = PAGES.map((p) => p.path.replace(':brand', SAMPLE_BRAND_ID));

const byKey = new Map(PAGES.map((p) => [p.key, p]));

/** `pathFor('rankings', brand.id)` → "/b/<id>/rankings". Brand screens need a brand id. */
export function pathFor(key: PageKey, brandId?: string | null): string {
  const p = byKey.get(key);
  if (!p) throw new Error(`Unknown page ${key}`);
  if (!p.brand) return p.path;
  if (!brandId) throw new Error(`pathFor('${key}') needs a brand id`);
  return p.path.replace(':brand', encodeURIComponent(brandId));
}

export function pageDef(key: PageKey): PageDef {
  return byKey.get(key)!;
}

/** The screen a pathname belongs to (exact or a sub-path of the longest matching route), or null. */
export function findPage(pathname: string): PageDef | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const parts = clean.split('/');
  let best: PageDef | null = null;
  let bestLen = -1;
  for (const p of PAGES) {
    const pat = p.path === '/' ? [''] : p.path.split('/');
    if (p.path === '/' ? clean !== '/' : parts.length < pat.length) continue;
    const ok = pat.every((seg, i) => seg.startsWith(':') ? !!parts[i] : seg === parts[i]);
    if (!ok) continue;
    if (p.end && parts.length !== pat.length && p.path !== '/') continue;
    if (pat.length > bestLen) {
      best = p;
      bestLen = pat.length;
    }
  }
  return best;
}

/** The brand id in a `/b/:brand/...` pathname, or null. */
export function brandIdFromPath(pathname: string): string | null {
  const m = /^\/b\/([^/]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]) : null;
}
